// React hooks for reading DuckSignals contract data
import { useReadContract, useReadContracts } from 'wagmi';
import { DUCK_SIGNALS_ADDRESS, DUCK_SIGNALS_ABI } from '../config/duckSignalsAbi';

// Signal and Prediction types
export interface OnChainSignal {
    agent: `0x${string}`;
    signalType: string;
    confidence: bigint;
    price: bigint;
    reason: string;
    timestamp: bigint;
}

export interface OnChainPrediction {
    agent: `0x${string}`;
    direction: string;
    confidence: bigint;
    referencePrice: bigint;
    targetTime: bigint;
    verified: boolean;
    correct: boolean;
    timestamp: bigint;
}

export interface AgentStats {
    name: string;
    totalSignals: bigint;
    totalPredictions: bigint;
    correctPredictions: bigint;
    lastActive: bigint;
    isRegistered: boolean;
}

/**
 * Hook to check if DuckSignals contract is deployed
 */
export function useIsContractDeployed() {
    const isDeployed = DUCK_SIGNALS_ADDRESS !== '0x0000000000000000000000000000000000000000';
    return { isDeployed, address: DUCK_SIGNALS_ADDRESS };
}

/**
 * Hook to get agent count from blockchain
 */
export function useAgentCount() {
    const { data, isLoading, error, refetch } = useReadContract({
        address: DUCK_SIGNALS_ADDRESS as `0x${string}`,
        abi: DUCK_SIGNALS_ABI,
        functionName: 'getAgentCount',
    });

    return {
        count: data ? Number(data) : 0,
        isLoading,
        error,
        refetch,
    };
}

/**
 * Hook to get all registered agents
 */
export function useAllAgents() {
    const { data, isLoading, error, refetch } = useReadContract({
        address: DUCK_SIGNALS_ADDRESS as `0x${string}`,
        abi: DUCK_SIGNALS_ABI,
        functionName: 'getAllAgents',
    });

    return {
        agents: data as `0x${string}`[] | undefined,
        isLoading,
        error,
        refetch,
    };
}

/**
 * Hook to get recent trading signals
 */
export function useRecentSignals(count: number = 10) {
    const { data, isLoading, error, refetch } = useReadContract({
        address: DUCK_SIGNALS_ADDRESS as `0x${string}`,
        abi: DUCK_SIGNALS_ABI,
        functionName: 'getRecentSignals',
        args: [BigInt(count)],
    });

    return {
        signals: data as OnChainSignal[] | undefined,
        isLoading,
        error,
        refetch,
    };
}

/**
 * Hook to get recent predictions
 */
export function useRecentPredictions(count: number = 10) {
    const { data, isLoading, error, refetch } = useReadContract({
        address: DUCK_SIGNALS_ADDRESS as `0x${string}`,
        abi: DUCK_SIGNALS_ABI,
        functionName: 'getRecentPredictions',
        args: [BigInt(count)],
    });

    return {
        predictions: data as OnChainPrediction[] | undefined,
        isLoading,
        error,
        refetch,
    };
}

/**
 * Hook to get total signals count
 */
export function useTotalSignals() {
    const { data, isLoading, error, refetch } = useReadContract({
        address: DUCK_SIGNALS_ADDRESS as `0x${string}`,
        abi: DUCK_SIGNALS_ABI,
        functionName: 'getTotalSignals',
    });

    return {
        total: data ? Number(data) : 0,
        isLoading,
        error,
        refetch,
    };
}

/**
 * Hook to get total predictions count
 */
export function useTotalPredictions() {
    const { data, isLoading, error, refetch } = useReadContract({
        address: DUCK_SIGNALS_ADDRESS as `0x${string}`,
        abi: DUCK_SIGNALS_ABI,
        functionName: 'getTotalPredictions',
    });

    return {
        total: data ? Number(data) : 0,
        isLoading,
        error,
        refetch,
    };
}

/**
 * Hook to get agent stats
 */
export function useAgentStats(agentAddress: `0x${string}` | undefined) {
    const { data, isLoading, error, refetch } = useReadContract({
        address: DUCK_SIGNALS_ADDRESS as `0x${string}`,
        abi: DUCK_SIGNALS_ABI,
        functionName: 'agents',
        args: agentAddress ? [agentAddress] : undefined,
    });

    const stats = data ? {
        name: data[0],
        totalSignals: data[1],
        totalPredictions: data[2],
        correctPredictions: data[3],
        lastActive: data[4],
        isRegistered: data[5],
    } as AgentStats : undefined;

    return {
        stats,
        isLoading,
        error,
        refetch,
    };
}

/**
 * Hook to get latest signal from an agent
 */
export function useLatestSignal(agentAddress: `0x${string}` | undefined) {
    const { data, isLoading, error, refetch } = useReadContract({
        address: DUCK_SIGNALS_ADDRESS as `0x${string}`,
        abi: DUCK_SIGNALS_ABI,
        functionName: 'latestSignal',
        args: agentAddress ? [agentAddress] : undefined,
        query: {
            enabled: !!agentAddress,
        }
    });

    return {
        signal: data as OnChainSignal | undefined,
        isLoading,
        error,
        refetch,
    };
}

/**
 * Hook to get all dashboard data in one call
 */
export function useDashboardData() {
    const { isDeployed } = useIsContractDeployed();

    const results = useReadContracts({
        contracts: isDeployed ? [
            {
                address: DUCK_SIGNALS_ADDRESS as `0x${string}`,
                abi: DUCK_SIGNALS_ABI,
                functionName: 'getAgentCount',
            },
            {
                address: DUCK_SIGNALS_ADDRESS as `0x${string}`,
                abi: DUCK_SIGNALS_ABI,
                functionName: 'getTotalSignals',
            },
            {
                address: DUCK_SIGNALS_ADDRESS as `0x${string}`,
                abi: DUCK_SIGNALS_ABI,
                functionName: 'getTotalPredictions',
            },
            {
                address: DUCK_SIGNALS_ADDRESS as `0x${string}`,
                abi: DUCK_SIGNALS_ABI,
                functionName: 'getRecentSignals',
                args: [BigInt(5)],
            },
            {
                address: DUCK_SIGNALS_ADDRESS as `0x${string}`,
                abi: DUCK_SIGNALS_ABI,
                functionName: 'getRecentPredictions',
                args: [BigInt(5)],
            },
        ] : [],
    });

    const isLoading = results.isLoading;
    const data = results.data;

    return {
        isDeployed,
        isLoading,
        agentCount: data?.[0]?.result ? Number(data[0].result) : 0,
        totalSignals: data?.[1]?.result ? Number(data[1].result) : 0,
        totalPredictions: data?.[2]?.result ? Number(data[2].result) : 0,
        recentSignals: data?.[3]?.result as OnChainSignal[] | undefined,
        recentPredictions: data?.[4]?.result as OnChainPrediction[] | undefined,
        refetch: results.refetch,
    };
}
